import { create } from "zustand";
import { devtools } from "zustand/middleware";

export type ConnectionLifecycleState =
  | "connecting"
  | "connected"
  | "disconnected"
  | "reconnecting";

export interface ConnectionHealthSnapshot {
  connectionState: ConnectionLifecycleState;
  connecting: boolean;
  connected: boolean;
  disconnected: boolean;
  reconnecting: boolean;
  lastConnectedAt: number | null;
  lastDisconnectedAt: number | null;
  latency: number;
  serverTime: number | null;
  reconnectCount: number;
  connectionDurationMs: number;
  apiConnected: boolean;
  signalrConnected: boolean;
}

interface ConnectionStoreState extends ConnectionHealthSnapshot {
  setConnectionState: (state: ConnectionLifecycleState) => void;
  setApiConnected: (value: boolean) => void;
  setSignalrConnected: (value: boolean) => void;
  setLatency: (value: number) => void;
  setServerTime: (value: number | null) => void;
  incrementReconnectCount: () => void;
  setReconnectCount: (value: number) => void;
  setConnectionDuration: (value: number) => void;
  markConnected: (timestamp?: number) => void;
  markDisconnected: (timestamp?: number) => void;
  markReconnecting: () => void;
}

const initialState: ConnectionHealthSnapshot = {
  connectionState: "disconnected",
  connecting: false,
  connected: false,
  disconnected: true,
  reconnecting: false,
  lastConnectedAt: null,
  lastDisconnectedAt: null,
  latency: 0,
  serverTime: null,
  reconnectCount: 0,
  connectionDurationMs: 0,
  apiConnected: true,
  signalrConnected: false,
};

export const useConnectionStore = create<ConnectionStoreState>()(
  devtools((set, get) => ({
    ...initialState,
    setConnectionState: (state) => {
      const now = Date.now();

      set((current) => ({
        connectionState: state,
        connecting: state === "connecting",
        connected: state === "connected",
        disconnected: state === "disconnected",
        reconnecting: state === "reconnecting",
        lastConnectedAt:
          state === "connected" ? current.lastConnectedAt ?? now : current.lastConnectedAt,
        lastDisconnectedAt:
          state === "disconnected" ? now : current.lastDisconnectedAt,
        signalrConnected: state === "connected",
        connectionDurationMs:
          state === "connected" && current.lastConnectedAt
            ? Math.max(0, now - current.lastConnectedAt)
            : state === "connected"
              ? 0
              : current.connectionDurationMs,
      }));
    },
    setApiConnected: (value) => set({ apiConnected: value }),
    setSignalrConnected: (value) => set({ signalrConnected: value }),
    setLatency: (value) => set({ latency: value }),
    setServerTime: (value) => set({ serverTime: value }),
    incrementReconnectCount: () =>
      set((state) => ({ reconnectCount: state.reconnectCount + 1 })),
    setReconnectCount: (value) => set({ reconnectCount: value }),
    setConnectionDuration: (value) => set({ connectionDurationMs: value }),
    markConnected: (timestamp = Date.now()) => {
      set((state) => ({
        connectionState: "connected",
        connecting: false,
        connected: true,
        disconnected: false,
        reconnecting: false,
        lastConnectedAt: state.lastConnectedAt ?? timestamp,
        signalrConnected: true,
        apiConnected: true,
        connectionDurationMs: 0,
      }));
    },
    markDisconnected: (timestamp = Date.now()) => {
      set((state) => ({
        connectionState: "disconnected",
        connecting: false,
        connected: false,
        disconnected: true,
        reconnecting: false,
        lastDisconnectedAt: timestamp,
        signalrConnected: false,
        connectionDurationMs: state.lastConnectedAt
          ? Math.max(0, timestamp - state.lastConnectedAt)
          : 0,
      }));
    },
    markReconnecting: () =>
      set(() => ({
        connectionState: "reconnecting",
        connecting: false,
        connected: false,
        disconnected: false,
        reconnecting: true,
        signalrConnected: false,
      })),
  })),
);

export function getConnectionHealth(): ConnectionHealthSnapshot {
  return useConnectionStore.getState();
}
